import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:1337/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Our Culture API Service (Single Type)
export const ourCultureService = {
  // Get our culture page data (Single Type - returns single object)
  async getOurCultureData(params = {}) {
    try {
      // Use simple API call - the custom controller will handle proper populate
      const response = await api.get('/our-culture');
      // For Single Types, the data is directly in response.data.data
      return {
        data: response.data.data,
        meta: response.data.meta,
        success: true
      };
    } catch (error) {
      console.error('Error fetching our culture data:', error);
      console.error('Error details:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Error message:', error.message);
      
      return {
        data: null,
        success: false,
        error: error.response?.data?.error?.message || error.message || 'Failed to fetch our culture data'
      };
    }
  }
};

// Helper function to format image URLs (same as aboutUsService and contactService)
export const formatImageUrl = (imageData) => {
  if (!imageData) return null;
  
  // Use base Strapi URL (without /api) for images
  const baseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:1337';
  
  // Handle different image data structures
  if (typeof imageData === 'string') {
    // If it's already a URL string
    if (imageData.startsWith('http')) {
      return imageData;
    }
    // If it's a relative path, prepend base URL
    return `${baseUrl}${imageData}`;
  }
  
  if (imageData && typeof imageData === 'object') {
    // Handle Strapi v4/v5 image format
    if (imageData.url) {
      return imageData.url.startsWith('http') 
        ? imageData.url 
        : `${baseUrl}${imageData.url}`;
    }
    
    // Handle formats (responsive images)
    if (imageData.formats) {
      // Priority: large > medium > small > thumbnail > original
      const format = imageData.formats.large || 
                    imageData.formats.medium || 
                    imageData.formats.small || 
                    imageData.formats.thumbnail;
      if (format && format.url) {
        return format.url.startsWith('http') 
          ? format.url 
          : `${baseUrl}${format.url}`;
      }
    }
    
    // Fallback to original image URL
    if (imageData.url) {
      return imageData.url.startsWith('http') 
        ? imageData.url 
        : `${baseUrl}${imageData.url}`;
    }
  }
  
  return null;
};

// Helper function to get the best image format for performance
export const getBestImageFormat = (imageData, preferredFormat = 'medium') => {
  if (!imageData || !imageData.formats) {
    return formatImageUrl(imageData);
  }
  
  const baseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:1337';
  
  // Get the preferred format or fallback to available formats
  const format = imageData.formats[preferredFormat] || 
                imageData.formats.medium || 
                imageData.formats.large || 
                imageData.formats.small || 
                imageData.formats.thumbnail;
  
  if (format && format.url) {
    return format.url.startsWith('http') 
      ? format.url 
      : `${baseUrl}${format.url}`;
  }
  
  // Fallback to original image
  return formatImageUrl(imageData);
};

// Helper function to extract slider images from our culture data
export const extractSliderImages = (ourCultureData) => {
  if (!ourCultureData || !ourCultureData.images) {
    return [];
  }
  
  return ourCultureData.images.map(imageItem => {
    const imageData = imageItem.images;
    return {
      id: imageItem.id,
      src: formatImageUrl(imageData), // Use 'src' instead of 'url' for SliderImages component
      url: formatImageUrl(imageData), // Keep 'url' for backward compatibility
      alt: imageData?.alternativeText || imageData?.name || 'Our Culture Image',
      caption: imageData?.caption || '',
      // Include different sizes for responsive images
      thumbnail: getBestImageFormat(imageData, 'thumbnail'),
      small: getBestImageFormat(imageData, 'small'),
      medium: getBestImageFormat(imageData, 'medium'),
      large: getBestImageFormat(imageData, 'large'),
    };
  });
};

// Helper function to format header data
export const formatHeaderData = (headerData) => {
  if (!headerData) return null;
  
  return {
    title: headerData.title || '',
    description: headerData.description || '',
    image: headerData.headerImage ? formatImageUrl(headerData.headerImage) : null
  };
};

// Helper function to format culture grids
export const formatCultureGrids = (cultureGrids) => {
  if (!cultureGrids || !Array.isArray(cultureGrids)) {
    return [];
  }
  
  return cultureGrids.map(grid => ({
    id: grid.id,
    title: grid.title || '',
    description: grid.description || '',
    image: grid.image ? formatImageUrl(grid.image) : null
  }));
};

// Helper function to format company initiatives
export const formatCompanyInitiatives = (initiatives) => {
  if (!initiatives || !Array.isArray(initiatives)) {
    return [];
  }
  
  return initiatives.map(initiative => ({
    id: initiative.id,
    title: initiative.title || '',
    description: initiative.description || '',
    image: initiative.image ? formatImageUrl(initiative.image) : 
           initiative.headerImage ? formatImageUrl(initiative.headerImage) : null
  }));
};

// Helper function to format image grid as company initiatives (fallback)
export const formatImageGridAsInitiatives = (imageGrid) => {
  if (!imageGrid || !Array.isArray(imageGrid)) {
    return [];
  }
  
  return imageGrid.map(item => ({
    id: item.id,
    title: item.title || '',
    description: item.description || '',
    image: item.headerImage ? formatImageUrl(item.headerImage) : null
  }));
};

// Helper function to format workplace survey data
export const formatWorkplaceSurvey = (surveyData) => {
  if (!surveyData) return null;
  
  const iconMap = {
    flexibility: 'FlexibilityIcon',
    diversity: 'DiversityIcon', 
    support: 'SupportIcon',
    growth: 'GrowthIcon'
  };
  
  return {
    surveyQuestion: surveyData.surveyQuestion || "What's Most Important in Workplace Culture?",
    surveySubtitle: surveyData.surveySubtitle || "Help us understand what matters most to you",
    thankYouMessage: surveyData.thankYouMessage || "Thank you for your Voting",
    options: surveyData.surveyOptions ? surveyData.surveyOptions.map(option => ({
      id: option.iconType,
      title: option.title,
      description: option.description,
      icon: iconMap[option.iconType] || 'FlexibilityIcon'
    })) : []
  };
};

export default ourCultureService;
